import type { TechnicalInquiryUpdateHookForm } from './validationSchema';
import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import styled from '@emotion/styled';
import { t } from 'i18next';
import { Dialog } from '@components/Dialog';
import { Button } from '@components/index';
import { SecureSubmitButton } from '@components/buttons/SecureSubmitButton';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import type { TechnicalInquiryDTOModel } from '@repositories/technicalSupportRepository/models/TechnicalInquiryDTO';
import type {
  TechnicalInquiryHistoryCreateDTO,
  TechnicalInquiryUpdateDTO,
} from '@repositories/technicalSupportRepository/technicalSupport.types';
import { useCreateTechnicalInquiryHistory, usePutTechnicalInquiryUpdate } from '@queryHooks/useTechnicalSupport';
import { useTechnicalSupportUpdateForm } from './validationSchema';

type Props = {
  technicalInquiry?: TechnicalInquiryDTOModel;
  open: boolean;
  onClose: () => void;
  isUpdateMode?: boolean;
};

export const TechnicalSupportUpdateModal = ({ technicalInquiry, open, onClose, isUpdateMode }: Props) => {
  const {
    uiStore: { toastStore },
  } = useStore();
  const { mutate: technicalInquiryUpdate, isPending: isUpdatePending } = usePutTechnicalInquiryUpdate();
  const { mutate: technicalInquiryHistoryCreate, isPending: isCreatePending } = useCreateTechnicalInquiryHistory();
  const isPending = isUpdatePending || isCreatePending;

  const { tittle, success } = isUpdateMode
    ? {
        tittle: t('Technical_Support.Update_Title'),
        success: (data: TechnicalInquiryUpdateHookForm) => {
          const rqData: TechnicalInquiryUpdateDTO = {
            inquiry: data.inquiry,
            technicalInquiryId: technicalInquiry?.technicalInquiryId,
          };
          if (technicalInquiry) {
            technicalInquiryUpdate(
              { technicalInquiryId: technicalInquiry?.technicalInquiryId, rqData },
              {
                onSuccess: () => {
                  onClose();
                  reset();
                  toastStore.open(t('Technical_Support.Update_Complete'));
                },
              },
            );
          }
        },
      }
    : {
        tittle: t('Technical_Support.Title'),
        success: (data: TechnicalInquiryUpdateHookForm) => {
          if (technicalInquiry) {
            const rqData: TechnicalInquiryHistoryCreateDTO = {
              managerId: technicalInquiry?.histories[0].managerId,
              status: 'CREATED',
              comment: data.inquiry,
              technicalInquiryId: technicalInquiry?.technicalInquiryId,
            };
            technicalInquiryHistoryCreate(rqData, {
              onSuccess: () => {
                onClose();
                reset();
                toastStore.open(t('Technical_Support.Inquiry_Complete'));
              },
            });
          }
        },
      };

  const { handleSubmit, reset, register, control, formState } = useTechnicalSupportUpdateForm();

  const [inquiry] = useWatch({
    control,
    name: ['inquiry'],
  });

  useEffect(() => {
    if (technicalInquiry && isUpdateMode) {
      reset({
        inquiry: technicalInquiry.inquiry,
      });
    }
  }, [reset, technicalInquiry, open, isUpdateMode]);

  return (
    <Dialog open={open} onClose={onClose} title={tittle} size="medium" height={600}>
      <UpdateForm onSubmit={handleSubmit(success)}>
        <InfoPosition>
          <InfoBox>
            <TextLi>{t('Technical_Support.Contact_Notice')}</TextLi>
            <TextLi>{t('Technical_Support.Admin_Notice')}</TextLi>
          </InfoBox>
        </InfoPosition>
        <InputBox>
          <MemoText>
            <InputText>
              {t('Technical_Support.Inquiry_Content')}
              <EssentialMark>*</EssentialMark>
            </InputText>
            <TextCount>{`(${inquiry?.length ?? 0}/300)`}</TextCount>
          </MemoText>
          <MemoBox placeholder={t('Technical_Support.Inquiry_Placeholder')} maxLength={300} {...register('inquiry')} />
          {formState.errors.inquiry?.message && <ErrorText>{formState.errors.inquiry?.message}</ErrorText>}
        </InputBox>
        <ButtonBox>
          <SecureSubmitButton isPending={isPending} type="submit" variant="contain" size="extraLarge" paddingHorizontal={90}>
            {t('Technical_Support.Complete')}
          </SecureSubmitButton>
          <Button variant="outline" size="extraLarge" paddingHorizontal={90} onClick={onClose}>
            {t('Technical_Support.Cancel')}
          </Button>
        </ButtonBox>
      </UpdateForm>
    </Dialog>
  );
};

const UpdateForm = styled.form`
  position: relative;
  height: 500px;
`;

const InfoPosition = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const InfoBox = styled.div`
  padding: 12px 33px 12px 28px;
  width: 424px;
  border-radius: 5px;
  background-color: ${({ theme }) => theme.colors['bg-gray-light']};
`;

const TextLi = styled.li`
  position: relative;
  ${fonts.Body2}
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
  list-style-type: none;
  white-space: pre;
  &::before {
    position: absolute;
    content: '•';
    color: ${({ theme }) => theme.colors['ic-gray-light']};
    font-size: 1.5em;
    left: -15px;
    top: -2px;
    opacity: 30%;
  }
`;

const InputBox = styled.div`
  margin-bottom: 24px;
`;

const MemoText = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const TextCount = styled.div`
  ${fonts.Body4}
  color: ${({ theme }) => theme.colors['text-gray-light']};
`;

const InputText = styled.span`
  ${fonts.Headline8}
`;

const MemoBox = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  height: 200px;
  resize: none;
  border: 1px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  border-radius: 2px;
`;

const ButtonBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  bottom: 0;
  width: 100%;
  gap: 8px;
`;

const ErrorText = styled.span`
  margin: 0;
  color: ${({ theme }) => theme.colors['text-red-light']};
  ${fonts.Body4};
`;

export const EssentialMark = styled.span`
  margin-left: 2px;
  color: ${({ theme }) => theme.colors['ic-red-light']};
`;
